import React, { useState, useEffect } from 'react';

const C = { bg:'#040000', bg2:'#0e0101', gold:'#f5c842', gold2:'#d4a843', gold3:'#b8922e', red:'#cc0000', red2:'#880000', cream:'#f0e6cc', cream2:'#c8b89a', muted:'#8a7060', border:'rgba(212,168,67,0.15)', green:'#00d480' };

const LOCALE_CURRENCY = {
  'en-IN':'INR','ta-IN':'INR','hi-IN':'INR','te-IN':'INR','ml-IN':'INR',
  'en-US':'USD','en-GB':'GBP','en-AU':'AUD','en-CA':'CAD',
  'en-SG':'SGD','ms-MY':'MYR','en-MY':'MYR',
  'ar-AE':'AED','en-AE':'AED',
  'de-DE':'EUR','fr-FR':'EUR','es-ES':'EUR',
};

// amounts in smallest unit (paise for INR, cents for others)
const CURRENCY_CONFIG = {
  INR: { symbol:'\u20b9',  monthly:49,    yearly:499,   monthlyPaise:4900,  yearlyPaise:49900,  flag:'\ud83c\uddee\ud83c\uddf3' },
  USD: { symbol:'$',   monthly:2.99,  yearly:19.99, monthlyPaise:299,   yearlyPaise:1999,   flag:'\ud83c\uddfa\ud83c\uddf8' },
  GBP: { symbol:'\u00a3',  monthly:2.49,  yearly:16.99, monthlyPaise:249,   yearlyPaise:1699,   flag:'\ud83c\uddec\ud83c\udde7' },
  AUD: { symbol:'A$',  monthly:4.49,  yearly:29.99, monthlyPaise:449,   yearlyPaise:2999,   flag:'\ud83c\udde6\ud83c\uddfa' },
  CAD: { symbol:'C$',  monthly:3.99,  yearly:26.99, monthlyPaise:399,   yearlyPaise:2699,   flag:'\ud83c\udde8\ud83c\udde6' },
  SGD: { symbol:'S$',  monthly:3.99,  yearly:26.99, monthlyPaise:399,   yearlyPaise:2699,   flag:'\ud83c\uddf8\ud83c\uddec' },
  MYR: { symbol:'RM',  monthly:12.99, yearly:89.99, monthlyPaise:1299,  yearlyPaise:8999,   flag:'\ud83c\uddf2\ud83c\uddfe' },
  AED: { symbol:'AED', monthly:10.99, yearly:74.99, monthlyPaise:1099,  yearlyPaise:7499,   flag:'\ud83c\udde6\ud83c\uddea' },
  EUR: { symbol:'\u20ac',  monthly:2.79,  yearly:18.99, monthlyPaise:279,   yearlyPaise:1899,   flag:'\ud83c\uddea\ud83c\uddfa' },
};

function detectCurrency() {
  try {
    const locale = (navigator.language || 'en-IN');
    if (LOCALE_CURRENCY[locale]) return LOCALE_CURRENCY[locale];
    if (locale.indexOf('IN') !== -1) return 'INR';
    const region = locale.split('-')[1];
    const map = { US:'USD', GB:'GBP', AU:'AUD', CA:'CAD', SG:'SGD', MY:'MYR', AE:'AED', DE:'EUR', FR:'EUR', ES:'EUR' };
    return map[region] || 'INR';
  } catch (e) { return 'INR'; }
}

function getPlans(currency) {
  const c = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG['INR'];
  const savings = (c.monthly * 12 - c.yearly).toFixed(2);
  return [
    {
      id:'monthly', name:'Monthly', price:c.monthly, symbol:c.symbol, currency,
      amount:c.monthlyPaise, duration:'30 days', tag:'POPULAR', tagColor:C.gold,
      features:['Full OTT Access','Tamil News Bulletin','FM Radio \u2014 Tamil stations','Ad-free viewing','HD Quality streaming','All devices'],
    },
    {
      id:'yearly', name:'Yearly', price:c.yearly, symbol:c.symbol, currency,
      amount:c.yearlyPaise, duration:'365 days', tag:'BEST VALUE', tagColor:C.green,
      features:['Everything in Monthly','Save ' + c.symbol + savings + ' vs Monthly','Priority Support','Early Access to Features','Exclusive Tamil content','Download & watch offline'],
    },
  ];
}

function Pricing({ onBack, user, isPaidMode, onSubscribed }) {
  const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID || '';
  const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://nethajiprime-backend.onrender.com';
  const [loading, setLoading] = useState(false);
  const [payError, setPayError] = useState(null);
  const [currency, setCurrency] = useState('INR');
  const [plans, setPlans] = useState(getPlans('INR'));
  const [selectedPlan, setSelectedPlan] = useState('monthly');

  useEffect(() => {
    const cur = detectCurrency();
    setCurrency(cur);
    setPlans(getPlans(cur));
  }, []);

  useEffect(() => {
    if (window.Razorpay || !RAZORPAY_KEY) return;
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    document.head.appendChild(s);
  }, [RAZORPAY_KEY]);

  const plan = plans.find(p => p.id === selectedPlan) || plans[0];

  const handleSubscribe = async () => {
    if (!RAZORPAY_KEY) { alert('Payment is being set up. Please try again shortly.'); return; }
    if (!user) { alert('Please login first to subscribe.'); return; }
    if (!plan) return;
    setLoading(true); setPayError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(API_URL + '/payments/create-order', {
        method:'POST',
        headers:{ 'Content-Type':'application/json', 'Authorization':'Bearer ' + token },
        body: JSON.stringify({ plan: plan.id, currency: plan.currency, amount: plan.amount }),
      });
      if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.detail || 'Order creation failed'); }
      const order = await res.json();
      const rzp = new window.Razorpay({
        key: order.key || RAZORPAY_KEY,
        amount: order.amount,
        currency: order.currency || plan.currency,
        name: 'Nethaji Prime OTT',
        description: plan.name + ' Subscription',
        order_id: order.order_id,
        prefill: { name:(user.full_name || user.name || ''), email:(user.email || '') },
        theme: { color:'#f5c842' },
        handler: async (response) => {
          const vres = await fetch(API_URL + '/payments/verify', {
            method:'POST',
            headers:{ 'Content-Type':'application/json', 'Authorization':'Bearer ' + token },
            body: JSON.stringify(Object.assign({}, response, { plan: plan.id, currency: plan.currency })),
          });
          setLoading(false);
          if (vres.ok) {
            alert('Payment successful! Welcome to Nethaji Prime ' + plan.name + '!');
            if (onSubscribed) onSubscribed();
          } else {
            setPayError('Payment verification failed. Contact support.');
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      });
      rzp.open();
    } catch (e) {
      setLoading(false);
      setPayError(e.message || 'Payment failed. Try again.');
    }
  };

  const fmt = (n) => (Number.isInteger(n) ? n : n.toFixed(2));

  return (
    <div style={{ minHeight:'100vh', background:C.bg, color:C.cream, fontFamily:"'Barlow',sans-serif" }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 4%', borderBottom:'1px solid rgba(212,168,67,0.2)', background:'#000', position:'sticky', top:0, zIndex:100 }}>
        <button onClick={onBack} style={{ background:'rgba(212,168,67,0.1)', border:'1px solid rgba(212,168,67,0.3)', color:C.gold2, cursor:'pointer', fontSize:'13px', fontWeight:700, padding:'7px 14px', borderRadius:'20px' }}>{'\u2190'} Back</button>
        <span style={{ color:C.gold, fontSize:'14px', fontWeight:900, letterSpacing:'2px', fontFamily:"'Barlow Condensed',sans-serif" }}>SUBSCRIBE</span>
        <span style={{ background:'rgba(245,200,66,0.1)', border:'1px solid rgba(245,200,66,0.3)', borderRadius:'12px', padding:'4px 10px', color:C.gold, fontSize:'11px', fontWeight:900 }}>{(CURRENCY_CONFIG[currency]||{}).flag} {currency}</span>
      </div>

      {/* Hero */}
      <div style={{ textAlign:'center', padding:'36px 4% 20px' }}>
        <div style={{ fontSize:'44px', marginBottom:'8px' }}>{'\ud83c\udfac'}</div>
        <h1 style={{ color:C.gold, fontSize:'clamp(1.5em,4vw,2.2em)', fontWeight:900, letterSpacing:'1px', fontFamily:"'Cinzel',serif", margin:'0 0 8px' }}>Choose Your Plan</h1>
        <p style={{ color:C.cream2, fontSize:'14px', margin:0 }}>Unlimited access to all Tamil political content</p>
        <div style={{ display:'inline-block', marginTop:'12px', background:'rgba(0,180,100,0.1)', border:'1px solid rgba(0,180,100,0.3)', borderRadius:'20px', padding:'6px 14px', color:C.green, fontSize:'11px', fontWeight:700 }}>{'\ud83d\udd12'} Secure payment via Razorpay</div>
      </div>

      {/* Plan cards */}
      <div style={{ maxWidth:'900px', margin:'0 auto', display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:'16px', padding:'0 4% 8px' }}>
        {plans.map((p) => {
          const on = selectedPlan === p.id;
          return (
            <div key={p.id} onClick={() => setSelectedPlan(p.id)} style={{ position:'relative', background: on ? 'rgba(245,200,66,0.05)' : C.bg2, border:'1px solid ' + (on ? C.gold : C.border), borderRadius:'14px', padding:'22px', cursor:'pointer', transition:'all 0.2s' }}>
              {p.tag && <span style={{ position:'absolute', top:'-10px', right:'18px', background:p.tagColor, color:'#000', fontSize:'9px', fontWeight:900, letterSpacing:'1px', padding:'3px 10px', borderRadius:'10px' }}>{p.tag}</span>}
              <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'14px' }}>
                <div style={{ width:'20px', height:'20px', borderRadius:'50%', border:'2px solid ' + (on ? C.gold : '#555'), display:'flex', alignItems:'center', justifyContent:'center' }}>{on && <div style={{ width:'10px', height:'10px', borderRadius:'50%', background:C.gold }} />}</div>
                <div>
                  <div style={{ color:'#fff', fontSize:'18px', fontWeight:900 }}>{p.name}</div>
                  <div style={{ color:'#888', fontSize:'12px' }}>{p.duration}</div>
                </div>
              </div>
              <div style={{ marginBottom:'14px' }}>
                <span style={{ color:C.gold, fontSize:'30px', fontWeight:900 }}>{p.symbol}{fmt(p.price)}</span>
                <span style={{ color:'#888', fontSize:'12px' }}> / {p.id === 'monthly' ? 'month' : 'year'}</span>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:'7px' }}>
                {p.features.map((f) => (
                  <div key={f} style={{ color:'#ccc', fontSize:'13px', display:'flex', gap:'8px' }}><span style={{ color:C.green }}>{'\u2713'}</span><span>{f}</span></div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {payError && <div style={{ maxWidth:'900px', margin:'12px auto 0', padding:'0 4%', color:'#ff6b6b', fontSize:'13px', textAlign:'center' }}>{payError}</div>}

      {/* Subscribe button */}
      <div style={{ maxWidth:'900px', margin:'18px auto 8px', padding:'0 4%' }}>
        <button onClick={handleSubscribe} disabled={loading} style={{ width:'100%', padding:'16px', background: loading ? 'rgba(245,200,66,0.3)' : 'linear-gradient(135deg,#f5c842,#d4a843)', color:'#1a0505', border:'none', borderRadius:'10px', fontSize:'15px', fontWeight:900, letterSpacing:'1px', cursor: loading ? 'default' : 'pointer', fontFamily:"'Barlow Condensed',sans-serif" }}>
          {loading ? 'PROCESSING...' : ('SUBSCRIBE \u2014 ' + plan.symbol + fmt(plan.price) + ' / ' + (plan.id === 'monthly' ? 'month' : 'year'))}
        </button>
        <p style={{ color:'#555', fontSize:'11px', textAlign:'center', marginTop:'10px' }}>{'\ud83d\udd12'} Secure payment. Cancel anytime.</p>
      </div>

      {/* Currency switcher */}
      <div style={{ maxWidth:'900px', margin:'10px auto 40px', padding:'0 4%' }}>
        <div style={{ color:'rgba(245,200,66,0.45)', fontSize:'9px', fontWeight:900, letterSpacing:'2px', marginBottom:'8px' }}>SWITCH CURRENCY</div>
        <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
          {Object.keys(CURRENCY_CONFIG).map((cur) => {
            const on = currency === cur;
            return (
              <button key={cur} onClick={() => { setCurrency(cur); setPlans(getPlans(cur)); setSelectedPlan('monthly'); }} style={{ padding:'6px 12px', borderRadius:'16px', border:'1px solid ' + (on ? C.gold : 'rgba(245,200,66,0.2)'), background: on ? C.gold : 'transparent', color: on ? '#000' : 'rgba(245,200,66,0.7)', fontSize:'11px', fontWeight:700, cursor:'pointer' }}>
                {CURRENCY_CONFIG[cur].flag} {cur}
              </button>
            );
          })}
        </div>
      </div>

      {loading && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'16px', zIndex:9999 }}>
          <div style={{ color:C.gold, fontSize:'14px', fontWeight:700 }}>Processing payment...</div>
        </div>
      )}
    </div>
  );
}

export default Pricing;
